import React from 'react';
import { observer } from "mobx-react-lite";
import { useIntl } from "react-intl";
import { AppContext } from "../../contexts/AppContext";
import { Race } from "../../services/RacesService";
import { convertDistance, convertElevation } from "../../utils/UnitsUtils";
import styled from "@mui/material/styles/styled";
import Box from "@mui/material/Box";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Link from '@mui/icons-material/Link';
import DirectionsBike from '@mui/icons-material/DirectionsBike';
import DirectionsRun from '@mui/icons-material/DirectionsRun';
import { defaultSvg } from '../../utils/latlngSVG';
import { IconButton } from '@mui/material';

interface RaceItemProps {
    race: Race;
    onClick: () => void;
}

const RaceBox = styled(Box)(({ theme }) => ({
    outline: `solid ${theme.palette.divider}`,
    outlineWidth: 1,
    cursor: "pointer",
    "&:hover": {
        outline: `solid ${theme.palette.common.black}`,
    },
}));

const RaceImageContainer = styled(Box)(({ theme }) => ({
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.palette.grey[200],
    position: "relative",
}));

const RaceImage = styled("div")(({ theme }) => ({
    width: theme.spacing(20),
    height: theme.spacing(20),
    display: "flex",
    padding: theme.spacing(1),
    color: theme.palette.common.black,
    backgroundColor: theme.palette.grey[200],
    '& path': {
        strokeWidth: 15,
    },
}));

export const RaceItem: React.FC<RaceItemProps> = observer(({ race, onClick }) => {
    const intl = useIntl();
    const theme = useTheme();
    const isUpMD = useMediaQuery(theme.breakpoints.up('md'));
    const { userService } = React.useContext(AppContext);

    const distanceUnits = userService.user?.distanceUnits || "km";
    const elevationUnits = userService.user?.elevationUnits || "m";

    return (
        <Box flexBasis={isUpMD ? "50%" : "100%"} maxWidth={isUpMD ? "50%" : "100%"} paddingTop={4} paddingLeft={4}>
            <RaceBox
                display="flex"
                justifyContent="center"
                flexDirection={isUpMD ? "row" : "column"}
                padding={4}
                onClick={onClick}
            >
                <RaceImageContainer>
                    <RaceImage dangerouslySetInnerHTML={{ __html: race.svg || defaultSvg }} />
                    <Box position="absolute" top={8} left={8}>
                        {
                            race.sport === "bike" ? <DirectionsBike /> : <DirectionsRun />
                        }
                    </Box>
                    <Box position="absolute" top={8} right={8}>
                        {
                            <IconButton href={race.website} target="_blank" rel="noopener noreferrer" size="small">
                                <Link />
                            </IconButton>
                        }
                    </Box>
                </RaceImageContainer>

                <Grid container marginX={isUpMD ? 4 : 0} marginTop={isUpMD ? 0 : 4}>
                    <Grid item xs={12}>
                        <Typography variant="h6">{race.name}</Typography>
                    </Grid>
                    <Grid item xs={6} marginY={1}>
                        <Typography variant="body2">{intl.formatMessage({ id: "data.cityTitle" })}</Typography>
                        <Typography variant="body2" fontSize={18}>
                            {race.city}, {race.country}
                        </Typography>
                    </Grid>
                    {race.distance && (
                        <Grid item xs={6} marginY={1}>
                            <Typography variant="body2">{intl.formatMessage({ id: "data.distanceTitle" })}</Typography>
                            <Typography variant="body2" fontSize={18}>
                                {intl.formatMessage(
                                    { id: "data.valueUnit" },
                                    {
                                        value: convertDistance(race.distance, distanceUnits),
                                        unit: intl.formatMessage({ id: distanceUnits })
                                    }
                                )}
                            </Typography>
                        </Grid>
                    )}
                    {race.elevationGain && (
                        <Grid item xs={6} marginY={1}>
                            <Typography variant="body2">{intl.formatMessage({ id: "data.elevationGainTitle" })}</Typography>
                            <Typography variant="body2" fontSize={18}>
                                {intl.formatMessage(
                                    { id: "data.valueUnit" },
                                    {
                                        value: convertElevation(race.elevationGain, elevationUnits),
                                        unit: intl.formatMessage({ id: elevationUnits })
                                    }
                                )}
                            </Typography>
                        </Grid>
                    )}
                    {race.elevationLoss && (
                        <Grid item xs={6} marginY={1}>
                            <Typography variant="body2">{intl.formatMessage({ id: "data.elevationLossTitle" })}</Typography>
                            <Typography variant="body2" fontSize={18}>
                                {intl.formatMessage(
                                    { id: "data.valueUnit" },
                                    {
                                        value: convertElevation(race.elevationLoss * -1, elevationUnits),
                                        unit: intl.formatMessage({ id: elevationUnits })
                                    }
                                )}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </RaceBox>
        </Box>
    );
});

export default RaceItem;